/*
 * Loyalty birthday offer eligibility – mirrors SFRA orceHelper/loyaltyHelper logic.
 * Uses customer profile attributes: c_birthdayMonth, c_BA25Redeemable, c_BA50Redeemable, c_BACouponExpirationDate.
 * Award period (days) mirrors SFRA: currentSite.getCustomPreferenceValue('loyalty-birthday-award-period') || 45.
 */
import {useMemo} from 'react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {
    BIRTHDAY_COUPON_CODES,
    BA25_COUPON_CODE,
    BA50_COUPON_CODE,
    BIRTHDAY_MONTHS,
    DEFAULT_BIRTHDAY_AWARD_PERIOD_DAYS
} from '../constants'

/**
 * Award period in days
 */
function getAwardPeriodDays() {
    const configValue = getConfig()?.app?.loyaltyBirthdayAwardPeriodDays
    const days = Number(configValue)
    return configValue != null && days > 0 ? days : DEFAULT_BIRTHDAY_AWARD_PERIOD_DAYS
}

/**
 * @param {string} [monthStr] - example "JAN"
 * @returns {number|null} 0-11 or null
 */
function getBirthdayMonthIndex(monthStr) {
    if (!monthStr || typeof monthStr !== 'string') return null
    const idx = BIRTHDAY_MONTHS.findIndex((m) => m.id === monthStr.toUpperCase())
    return idx >= 0 ? idx : null
}

/**
 * Award validity: from first day of birth month until expiration (profile or start + period).
 * @param {Object} customer - customer object with c_birthdayMonth, c_BACouponExpirationDate
 * @returns {{ start: Date, end: Date } | null}
 */
function getBirthdayAwardWindow(customer) {
    const monthIndex = getBirthdayMonthIndex(customer?.c_birthdayMonth)
    if (monthIndex == null) return null

    const now = new Date()
    const year = now.getFullYear()
    const start = new Date(year, monthIndex, 1)

    let end
    const expirationStr = customer?.c_BACouponExpirationDate
    if (expirationStr) {
        end = new Date(expirationStr)
        if (Number.isNaN(end.getTime())) end = null
    }
    if (!end) {
        end = new Date(start)
        end.setDate(end.getDate() + getAwardPeriodDays())
    }

    return {start, end}
}

/**
 * True if current date is within the birthday award window (birth month through expiration).
 * @param {Object} customer
 * @returns {boolean}
 */
function isEligibleForBirthdayAward(customer) {
    const awardWindow = getBirthdayAwardWindow(customer)
    if (!awardWindow) return false
    const now = new Date()
    return now >= awardWindow.start && now <= awardWindow.end
}

/**
 * Which birthday coupon codes are redeemable per profile flags.
 * @param {Object} customer
 * @returns {string[]} e.g. ['BA50'], ['BA25'], or ['BA25','BA50']
 */
function getRedeemableBirthdayCoupons(customer) {
    if (customer?.c_BA50Redeemable && customer?.c_BA25Redeemable)
        return [BA50_COUPON_CODE, BA25_COUPON_CODE]
    if (customer?.c_BA50Redeemable) return [BA50_COUPON_CODE]
    if (customer?.c_BA25Redeemable) return [BA25_COUPON_CODE]
    return []
}

/**
 * Codes already applied on the basket (BA25 / BA50 only).
 * @param {Object} basket
 * @returns {string[]}
 */
export function getAppliedBirthdayCoupons(basket) {
    const items = basket?.couponItems || []
    return items.map((item) => item.code).filter((code) => BIRTHDAY_COUPON_CODES.includes(code))
}

/**
 * Hook: eligibility + redeemable + already applied for the birthday card and apply action.
 * @param {Object} basket - current basket
 * @param {Object} customer - current customer (useCurrentCustomer().data)
 * @returns {{
 *   isEligibleForBirthdayOffer: boolean,
 *   redeemableCoupons: string[],
 *   appliedBirthdayCoupons: string[],
 *   showBirthdayCard: boolean
 * }}
 */
export function useLoyaltyBirthday(basket, customer) {
    return useMemo(() => {
        const isEligible = isEligibleForBirthdayAward(customer)
        const redeemableCoupons = getRedeemableBirthdayCoupons(customer)
        const appliedBirthdayCoupons = getAppliedBirthdayCoupons(basket)

        const hasRedeemable = redeemableCoupons.length > 0
        const notAllApplied = redeemableCoupons.some(
            (code) => !appliedBirthdayCoupons.includes(code)
        )
        const showBirthdayCard =
            !!customer?.customerId &&
            customer?.isRegistered &&
            isEligible &&
            hasRedeemable &&
            notAllApplied &&
            !appliedBirthdayCoupons?.length // We'll not display the birthday card if birthday coupon is already applied

        return {
            isEligibleForBirthdayOffer: isEligible && hasRedeemable,
            redeemableCoupons,
            appliedBirthdayCoupons,
            showBirthdayCard
        }
    }, [basket, customer])
}
